import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase, testSupabaseConnection } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

// Types for analysis data
export interface ContentAnalysis {
  id: string;
  title: string;
  video_url: string;
  thumbnail_url: string | null;
  upload_date: string;
  status: 'processing' | 'completed' | 'failed';
  grade: string | null;
  predicted_average_watch_percentage?: number;
  predicted_major_dropoff_timestamp?: string;
  created_at: string;
  updated_at: string;
}

interface AnalyticsContextType {
  analyses: ContentAnalysis[];
  loading: boolean;
  uploadVideo: (file: File, title: string) => Promise<string>;
  getAnalysis: (id: string) => ContentAnalysis | undefined;
  getRecentAnalyses: () => ContentAnalysis[];
  setAnalyses: React.Dispatch<React.SetStateAction<ContentAnalysis[]>>;
  refreshAnalyses: () => Promise<void>;
  forceUpdate: (analysisId: string) => Promise<void>;
}

export const AnalyticsContext = createContext<AnalyticsContextType>({
  analyses: [],
  loading: true,
  uploadVideo: async () => '',
  getAnalysis: () => undefined,
  getRecentAnalyses: () => [],
  setAnalyses: () => {},
  refreshAnalyses: async () => {},
  forceUpdate: async () => {}
});

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

// Cache for analyses data
const analysesCache = new Map<string, {
  data: ContentAnalysis[];
  timestamp: number;
}>();

// Cache TTL (5 minutes)
const ANALYSES_CACHE_TTL = 2 * 60 * 1000; // 2 minutes
// Track last refresh time to prevent too frequent refreshes
let lastRefreshTime = 0;
// Minimum time between refreshes (2 seconds)
const MIN_REFRESH_INTERVAL = 1000; // 1 second

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [analyses, setAnalyses] = useState<ContentAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, session } = useAuth(); // Get auth state
  const [lastError, setLastError] = useState<Error | null>(null);
  const [realtimeSubscription, setRealtimeSubscription] = useState<any>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Helper function to log errors and set state
  const handleError = (error: any, message: string) => {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    console.error(`${message}:`, errorObj);
    setLastError(errorObj);
    return errorObj;
  };

  // Check database connection
  const checkConnection = async () => {
    try {
      const connected = await testSupabaseConnection();
      return connected;
    } catch (err) {
      handleError(err, 'Database connection check failed');
      return false;
    }
  };

  // Force update a specific analysis
  const forceUpdate = useCallback(async (analysisId: string) => {
    if (!user || !analysisId) return;
    
    console.log(`Force updating analysis: ${analysisId}`);
    
    try {
      const { data, error } = await supabase
        .from('content_analyses')
        .select('*')
        .eq('id', analysisId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching analysis update:', error);
        return;
      }
      
      if (data) {
        console.log(`Analysis update received for ${analysisId}, status: ${data.status}`);
        
        setAnalyses(prev => {
          // Find if this analysis exists in the current list
          const existingIndex = prev.findIndex(a => a.id === analysisId);
          
          if (existingIndex >= 0) {
            // Replace the existing analysis with updated data
            const newAnalyses = [...prev];
            newAnalyses[existingIndex] = data;
            return newAnalyses;
          } else {
            // Add as a new analysis if not found
            return [data, ...prev];
          }
        });
      }
    } catch (err) {
      console.error('Error during force update:', err);
    }
  }, [user]);

  // Refresh analyses from Supabase with improved caching
  const refreshAnalyses = useCallback(async () => {
    if (!user) {
      setAnalyses([]);
      setLoading(false);
      return;
    }

    // Check if we're refreshing too frequently
    const now = Date.now();
    if (now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
      console.log('Skipping refresh - too soon since last refresh');
      return;
    }
    lastRefreshTime = now;

    // Check if we can use cached data
    const cachedData = analysesCache.get(user.id);
    if (cachedData && (now - cachedData.timestamp < ANALYSES_CACHE_TTL)) {
      console.log('Using cached analyses data');
      if (analyses.length === 0) {
        setAnalyses(cachedData.data);
      }
      setLoading(false);
      return;
    }

    console.log('Refreshing analyses for user:', user.id);
    setLoading(true);
    
    try {
      // First check connection
      const connected = await checkConnection();
      if (!connected) {
        throw new Error('Database connection failed');
      }
      
      const { data, error } = await supabase
        .from('content_analyses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }
      
      console.log(`Loaded ${data?.length || 0} analyses`);

      setAnalyses(data || []);
      
      // Update cache
      analysesCache.set(user.id, {
        data: data || [],
        timestamp: now
      });
      
      setLastError(null);
      setInitialLoadComplete(true);
    } catch (error) {
      handleError(error, 'Error refreshing analyses');
      
      // Keep existing analyses if fetch fails
      // This prevents UI flashing with empty state on temporary errors
      if (analyses.length === 0) {
        setAnalyses([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user, analyses.length]);

  // Setup realtime subscription only once
  useEffect(() => {
    // Only subscribe if there's a logged-in user and we haven't loaded data yet
    if (!user || initialLoadComplete) {
      return;
    }

    console.log('Initial load for user:', user.id);
    refreshAnalyses();

    // Clean up any existing subscription
    if (realtimeSubscription) {
      console.log('Cleaning up existing realtime subscription');
      realtimeSubscription.unsubscribe();
    }

    // Subscribe to changes with specific filter for this user
    const subscription = supabase
      .channel('content_analyses_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'content_analyses',
        filter: `user_id=eq.${user.id}` 
      }, payload => {
        console.log('Realtime update received:', payload.eventType, payload.new?.id, 'Status:', payload.new?.status);
        
        if (payload.eventType === 'INSERT') {
          setAnalyses(prev => {
            // Check if we already have this analysis (avoid duplicates)
            const exists = prev.some(a => a.id === payload.new.id);
            if (exists) return prev;
            return [payload.new as ContentAnalysis, ...prev];
          });
        } else if (payload.eventType === 'UPDATE') {
          // Special handling for status changes from processing to completed
          const oldStatus = payload.old?.status;
          const newStatus = payload.new?.status;
          
          if (oldStatus === 'processing' && newStatus === 'completed') {
            console.log(`Analysis ${payload.new.id} completed!`);
          }
          
          setAnalyses(prev => 
            prev.map(analysis => 
              analysis.id === payload.new.id ? payload.new as ContentAnalysis : analysis
            )
          );
        } else if (payload.eventType === 'DELETE') {
          setAnalyses(prev => 
            prev.filter(analysis => analysis.id !== payload.old.id)
          );
        }
      })
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    // Save subscription for cleanup
    setRealtimeSubscription(subscription);

    return () => {
      console.log('Cleaning up realtime subscription');
      subscription.unsubscribe();
    };
  }, [user, refreshAnalyses, initialLoadComplete, realtimeSubscription]);

  // Expose functions for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshAnalyses = refreshAnalyses;
      (window as any).forceUpdateAnalysis = forceUpdate;
    }
  }, [refreshAnalyses, forceUpdate]);

  // Upload a new video
  const uploadVideo = async (file: File, title: string): Promise<string> => {
    setLoading(true);
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Upload video to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `public/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get video URL
      const { data: { publicUrl } } = supabase.storage
        .from('videos')
        .getPublicUrl(filePath);

      // Create analysis entry
      const { data: analysis, error: dbError } = await supabase
        .from('content_analyses')
        .insert([
          {
            user_id: user.id,
            title,
            video_url: publicUrl,
            storage_id: fileName.split('.')[0],
            filename: file.name,
            status: 'processing'
          }
        ])
        .select()
        .single();

      if (dbError) throw dbError;
      
      console.log('Created new analysis:', analysis.id);
      
      // Update cache with new analysis
      const cachedData = analysesCache.get(user.id);
      if (cachedData) {
        analysesCache.set(user.id, {
          data: [analysis, ...cachedData.data],
          timestamp: Date.now()
        });
      }
      
      return analysis.id;
    } catch (error) {
      throw handleError(error, 'Upload error');
    } finally {
      setLoading(false);
    }
  };

  // Get a specific analysis by ID using the local cache
  const getAnalysis = (id: string): ContentAnalysis | undefined => {
    return analyses.find(analysis => analysis.id === id);
  };

  // Get recent analyses
  const getRecentAnalyses = (): ContentAnalysis[] => {
    return analyses.slice(0, 5);
  };

  return (
    <AnalyticsContext.Provider 
      value={{ 
        analyses, 
        loading, 
        uploadVideo, 
        getAnalysis,
        getRecentAnalyses,
        setAnalyses,
        refreshAnalyses,
        forceUpdate
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};